import { useRef, useState, type PointerEvent, type ReactNode } from 'react';
import { cx } from '../cx';

export type MapLayoutProps = {
  /** The results (ListRows), kept in sync with the map by the caller. */
  list: ReactNode;
  /** The map itself (muted basemap with MapPins and MapClusters). */
  map: ReactNode;
  /** Above the list: the view SegmentedControl and the count. */
  toolbar?: ReactNode;
  /** Accessible name of the phone sheet's handle ("Results list"). */
  handleLabel: string;
  className?: string;
};

export type MapSheetSnap = 'peek' | 'half' | 'full';
const SNAP_HEIGHT: Record<MapSheetSnap, string> = { peek: '18dvh', half: '50dvh', full: '88dvh' };

/** The snap point nearest to a sheet height (as a share of the viewport, 0 to 1). Pure, exported for tests. */
export function nearestSnap(share: number): MapSheetSnap {
  const points: Array<[MapSheetSnap, number]> = [
    ['peek', 0.18],
    ['half', 0.5],
    ['full', 0.88],
  ];
  return points.reduce((best, p) => (Math.abs(p[1] - share) < Math.abs(best[1] - share) ? p : best))[0];
}

/**
 * The map view: the list beside the map from md; on phones the map fills the screen and the list is
 * a sheet over it that can be dragged (or stepped with the handle) between peek, half and full.
 */
export function MapLayout({ list, map, toolbar, handleLabel, className }: MapLayoutProps) {
  const [snap, setSnap] = useState<MapSheetSnap>('half');
  const [dragHeight, setDragHeight] = useState<number | null>(null);
  const start = useRef<{ y: number; h: number } | null>(null);
  const sheetRef = useRef<HTMLDivElement>(null);

  const onDown = (e: PointerEvent<HTMLButtonElement>) => {
    start.current = { y: e.clientY, h: sheetRef.current?.getBoundingClientRect().height ?? 0 };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onMove = (e: PointerEvent<HTMLButtonElement>) => {
    if (start.current) setDragHeight(Math.max(80, start.current.h + (start.current.y - e.clientY)));
  };
  const onUp = () => {
    if (start.current && dragHeight !== null) setSnap(nearestSnap(dragHeight / window.innerHeight));
    start.current = null;
    setDragHeight(null);
  };
  const step = () => setSnap((s) => (s === 'peek' ? 'half' : s === 'half' ? 'full' : 'peek'));

  return (
    <div className={cx('relative flex h-dvh flex-col md:h-full md:min-h-160 md:flex-row', className)}>
      <div className="absolute inset-0 md:relative md:order-last md:flex-1">{map}</div>
      <div
        ref={sheetRef}
        data-snap={snap}
        style={{ ['--jc-sheet-h' as string]: dragHeight !== null ? `${dragHeight}px` : SNAP_HEIGHT[snap] }}
        className={cx(
          'absolute inset-x-0 bottom-0 z-(--jc-z-raised) flex h-(--jc-sheet-h) flex-col rounded-t-jc-container bg-jc-surface shadow-jc-lg',
          dragHeight === null &&
            'transition-all duration-(--jc-duration-base) ease-jc-out motion-reduce:transition-none',
          'md:static md:h-auto md:w-96 md:rounded-none md:shadow-none md:transition-none',
        )}
      >
        <button
          type="button"
          aria-label={handleLabel}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          onClick={step}
          className="flex h-6 shrink-0 touch-none items-center justify-center md:hidden"
        >
          <span className="h-1 w-10 rounded-jc-control bg-jc-border-control" />
        </button>
        {toolbar ? <div className="shrink-0 border-b border-jc-border px-4 py-3">{toolbar}</div> : null}
        <div className="min-h-0 flex-1 overflow-y-auto">{list}</div>
      </div>
    </div>
  );
}
